import type { QuizSubSubmitData } from '@repo/api/quiz/quizService';
import type { QuizAttemptPrivate } from '@repo/types/quiz';

import { useSaveQuiz } from '@repo/api/quiz/useSaveQuiz';
import { useSubmitQuiz } from '@repo/api/quiz/useSubmitQuiz';
import { useSubSubmitQuiz } from '@repo/api/quiz/useSubSubmitQuiz';
import { ErrorCodes } from '@repo/lib/errorCodes';
import { quizErrorMessages } from '@repo/lib/messages/quizErrorMessages';
import { isAxiosError } from 'axios';
import { useNavigate } from 'react-router';

import { useQuizQuickSave } from '@/hooks/quiz/useQuizQuickSave';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { useQuizResolverStore } from '@/store/quiz-store';

export function useQuizMutations(id: string, attemptId: string) {
  const navigate = useNavigate();
  const resultPageUrl: string = `/quizzes/${id}/attempt/${attemptId}/result`;
  const updateQuestionMarked = useQuizResolverStore((state) => state.updateQuestionMarked);
  const getExportedAnswers = useQuizResolverStore((state) => state.getExportedAnswers);
  const { quickSave } = useQuizQuickSave();
  const saveQuizMutation = useSaveQuiz(id, attemptId);
  const subSubmitQuizMutation = useSubSubmitQuiz(id, attemptId);
  const submitQuizMutation = useSubmitQuiz(id, attemptId);

  const submitQuiz = async (): Promise<QuizAttemptPrivate> => {
    return submitQuizMutation.mutateAsync().catch((error) => {
      const axiosError = isAxiosError(error);
      const code = error.response?.data.code;
      if (axiosError && (code === ErrorCodes.FORBIDDEN.QUIZ.ALREADY_COMPLETED || code === ErrorCodes.NOT_FOUND.QUIZ_ATTEMPT)) {
        navigate(resultPageUrl, { replace: true });
      }
      toasterMessageHandler(quizErrorMessages, error);
      throw error;
    });
  };

  const saveQuiz = async (errorCallback?: (error: unknown) => void): Promise<string> => {
    const answers = getExportedAnswers();
    return saveQuizMutation.mutateAsync({ answers }).catch(async (error) => {
      const axiosError = isAxiosError(error);
      const code = error.response?.data.code;
      if (axiosError && code === ErrorCodes.FORBIDDEN.QUIZ.OUTSIDE_AVAILBILITY) {
        toasterMessageHandler(quizErrorMessages, error);
        await submitQuiz();
        errorCallback?.(error);
      }
      throw error;
    });
  };

  const subSubmitQuiz = async (data: QuizSubSubmitData): Promise<void> => {
    await saveQuiz();
    await subSubmitQuizMutation
      .mutateAsync(data)
      .then((response) => {
        updateQuestionMarked(response);
        return response;
      })
      .catch((error) => {
        const axiosError = isAxiosError(error);
        const code = error.response?.data.code;
        if (axiosError && (code === ErrorCodes.FORBIDDEN.QUIZ.ALREADY_COMPLETED || code === ErrorCodes.NOT_FOUND.QUIZ_ATTEMPT)) {
          navigate(resultPageUrl, { replace: true });
        }

        toasterMessageHandler(quizErrorMessages, error);
        throw error;
      });
  };

  const onSaveAndSubmit = async (): Promise<void> => {
    await saveQuiz();
    await submitQuiz();
  };

  return {
    mutations: {
      saveQuizMutation,
      subSubmitQuizMutation,
      submitQuizMutation,
    },
    actions: {
      quickSaveQuiz: quickSave,
      submitQuiz,
      saveQuiz,
      subSubmitQuiz,
      onSaveAndSubmit,
    },
    state: {
      resultPageUrl,
      isPending: subSubmitQuizMutation.isPending || saveQuizMutation.isPending || submitQuizMutation.isPending,
    },
  };
}
